/**
 * ArticleSiteSettingsReadService
 *
 * READ-ONLY access to the project article settings singleton
 * (projects/{projectId}/articleSiteSettings/default). The owner of the writes is the
 * Segmently CLI / builder (`articles site-settings`); the editor only needs the topic
 * list to let an author point an ArticleIndex at a topic.
 */

import { doc, getDoc } from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';

/** One `articleSiteSettings.topics` entry: id + locale → label. */
export interface ArticleSiteTopic {
  id: string;
  label: Record<string, string>;
}

const COLLECTION = 'articleSiteSettings';
const SETTINGS_DOC = 'default';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Keeps only string labels; a topic without id or without a label map is dropped. */
function toTopic(value: unknown): ArticleSiteTopic | null {
  if (!isRecord(value) || typeof value.id !== 'string' || !value.id || !isRecord(value.label)) return null;
  const label = Object.fromEntries(Object.entries(value.label).filter(([, text]) => typeof text === 'string')) as Record<string, string>;
  return { id: value.id, label };
}

export class ArticleSiteSettingsReadService {
  private readonly firestore = CoreFirebaseService.getInstance().firestore;

  /** Topics of the project; a missing document or missing field is an empty list. */
  async readTopics(projectId: string): Promise<ArticleSiteTopic[]> {
    try {
      const snapshot = await getDoc(doc(this.firestore, `projects/${projectId}/${COLLECTION}`, SETTINGS_DOC));
      if (!snapshot.exists()) return [];
      const topics = (snapshot.data() as { topics?: unknown } | undefined)?.topics;
      if (!Array.isArray(topics)) return [];
      return topics.map(toTopic).filter((topic): topic is ArticleSiteTopic => topic !== null);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ArticleSiteSettingsReadService.readTopics');
      throw error;
    }
  }
}
